import { useStore } from 'zustand';
import { createStore } from 'zustand';
import type {
  ConditionKind,
  GroupOperator,
  NodePath,
  TreeConditionNode,
  TreeGroupNode,
} from '@/components/tree-dnd/tree-dnd-types';
import { applyTreeOp } from '@/components/tree-dnd/tree-dnd-apply-ops';
import { diffTrees, type TreeOp } from '@/components/tree-dnd/tree-dnd-diff';
import {
  INITIAL_TREE,
  appendChild,
  createConditionNode,
  createGroupNode,
  moveNode as moveNodeInTree,
  removeNodeAtPath,
  setNegatedAtPath,
  updateConditionAtPath,
  updateGroupOperatorAtPath,
} from '@/components/tree-dnd/tree-dnd-utils';

const OP_DELAY_MS = 150;
const MAX_STAGGERED_OPS = 12;
const HIGHLIGHT_MS = 400;

type TreeDndStore = {
  tree: TreeGroupNode;
  isApplyingAiChanges: boolean;
  highlightedNodeId: string | null;
  addCondition: (
    parentPath: NodePath,
    kind: ConditionKind
  ) => string | undefined;
  addGroup: (parentPath: NodePath, operator?: GroupOperator) => void;
  removeNode: (path: NodePath) => void;
  moveNode: (
    sourcePath: NodePath,
    destinationParentPath: NodePath,
    destinationIndex: number
  ) => void;
  updateCondition: (
    path: NodePath,
    patch: Partial<Pick<TreeConditionNode, 'negated' | 'args'>>
  ) => void;
  setGroupOperator: (path: NodePath, operator: GroupOperator) => void;
  setNegated: (path: NodePath, negated: boolean) => void;
  reset: () => void;
  setTree: (tree: TreeGroupNode) => void;
  applyTreeGradually: (nextTree: TreeGroupNode) => Promise<void>;
  clearHighlight: () => void;
};

let applyGeneration = 0;
let highlightTimeout: ReturnType<typeof setTimeout> | null = null;

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

function highlightIdFromOp(op: TreeOp): string | null {
  switch (op.type) {
    case 'add':
      return op.node.id;
    case 'update':
    case 'move':
      return op.id;
    case 'remove':
      return null;
  }
}

export const treeDndStore = createStore<TreeDndStore>((set, get) => ({
  tree: INITIAL_TREE,
  isApplyingAiChanges: false,
  highlightedNodeId: null,

  addCondition: (parentPath, kind) => {
    if (get().isApplyingAiChanges) return;
    const node = createConditionNode(kind);
    set((state) => ({
      tree: appendChild(state.tree, parentPath, node),
    }));
    return node.id;
  },

  addGroup: (parentPath, operator = 'AND') => {
    if (get().isApplyingAiChanges) return;
    set((state) => ({
      tree: appendChild(state.tree, parentPath, createGroupNode(operator)),
    }));
  },

  removeNode: (path) => {
    if (get().isApplyingAiChanges) return;
    // Root group cannot be removed — it is the forced single top-level group.
    if (path.length === 0) return;

    set((state) => {
      const next = removeNodeAtPath(state.tree, path);
      return next ? { tree: next } : state;
    });
  },

  moveNode: (sourcePath, destinationParentPath, destinationIndex) => {
    if (get().isApplyingAiChanges) return;
    set((state) => {
      const next = moveNodeInTree(
        state.tree,
        sourcePath,
        destinationParentPath,
        destinationIndex
      );
      return next ? { tree: next } : state;
    });
  },

  updateCondition: (path, patch) => {
    if (get().isApplyingAiChanges) return;
    set((state) => ({
      tree: updateConditionAtPath(state.tree, path, patch),
    }));
  },

  setGroupOperator: (path, operator) => {
    if (get().isApplyingAiChanges) return;
    // Root group is always AND and not user-editable.
    if (path.length === 0) return;
    set((state) => ({
      tree: updateGroupOperatorAtPath(state.tree, path, operator),
    }));
  },

  setNegated: (path, negated) => {
    if (get().isApplyingAiChanges) return;
    // Root group cannot be negated.
    if (path.length === 0) return;
    set((state) => ({
      tree: setNegatedAtPath(state.tree, path, negated),
    }));
  },

  reset: () => {
    set({ tree: INITIAL_TREE, highlightedNodeId: null });
  },

  setTree: (tree) => {
    if (tree.type !== 'group') return;
    set({
      tree: {
        ...tree,
        id: 'root',
        operator: 'AND',
        negated: false,
      },
    });
  },

  clearHighlight: () => {
    set({ highlightedNodeId: null });
  },

  applyTreeGradually: async (nextTree) => {
    if (nextTree.type !== 'group') return;

    const normalizedTree: TreeGroupNode = {
      ...nextTree,
      id: 'root',
      operator: 'AND',
      negated: false,
    };

    const generation = ++applyGeneration;
    const current = get().tree;
    const ops = diffTrees(current, normalizedTree);

    if (ops.length === 0) {
      set({ tree: normalizedTree });
      return;
    }

    set({ isApplyingAiChanges: true, highlightedNodeId: null });

    const staggered = ops.slice(0, MAX_STAGGERED_OPS);
    const remainder = ops.length > MAX_STAGGERED_OPS;

    try {
      for (const op of staggered) {
        if (generation !== applyGeneration) return;

        set((state) => ({
          tree: applyTreeOp(state.tree, op),
        }));

        const highlightId = highlightIdFromOp(op);
        if (highlightId) {
          if (highlightTimeout) clearTimeout(highlightTimeout);
          set({ highlightedNodeId: highlightId });
          highlightTimeout = setTimeout(() => {
            if (generation !== applyGeneration) return;
            if (get().highlightedNodeId === highlightId) {
              set({ highlightedNodeId: null });
            }
          }, HIGHLIGHT_MS);
        }

        await sleep(OP_DELAY_MS);
      }

      if (generation !== applyGeneration) return;

      // Always sync to the exact AI tree (covers capped ops + any drift).
      if (remainder || staggered.length > 0) {
        set({ tree: normalizedTree });
      }
    } finally {
      if (generation === applyGeneration) {
        set({ isApplyingAiChanges: false });
      }
    }
  },
}));

export function useTreeDndStore<T>(selector: (state: TreeDndStore) => T): T {
  return useStore(treeDndStore, selector);
}
